/**
 * Auth Service - Gestisce autenticazione con httpOnly Cookies
 * 
 * Questo servizio sostituisce le chiamate dirette a Cognito con chiamate
 * alla Lambda Auth Proxy che gestisce i cookie httpOnly.
 * 
 * I token NON sono più salvati in sessionStorage, ma vengono gestiti
 * automaticamente tramite cookie dal browser.
 * 
 * In sviluppo usa /api che viene proxy-ato a API Gateway (setupProxy.js)
 * In produzione usa l'URL assoluto di API Gateway
 */

import { AUTH_API_BASE_URL } from './apiConfig';

const API_BASE_URL = AUTH_API_BASE_URL;

export interface AuthUser {
    sub: string;
    email: string;
    email_verified?: boolean;
    phone_number?: string;
    ragione_sociale?: string;
    partita_iva?: string;
    codice_fiscale?: string;
    pec?: string;
    'cognito:groups'?: string[];
    role?: string;
}

export interface LoginResponse {
    success: boolean;
    user: AuthUser;
}

export interface StatusResponse {
    authenticated: boolean;
    user?: AuthUser;
}

/**
 * Login utente - I token vengono salvati in cookie httpOnly automaticamente
 */
export async function loginWithCookies(
    email: string, 
    password: string
): Promise<LoginResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/user/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include', // IMPORTANTE: Include i cookie nella richiesta/risposta
            body: JSON.stringify({ email, password })
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Login fallito');
        }
        
        const data = await response.json();
        
        // Salva informazioni utente in sessionStorage
        if (data.user) {
            sessionStorage.setItem('user', JSON.stringify(data.user));
        }
        
        console.log('✅ Login con cookie httpOnly completato');
        return data;
        
    } catch (error) {
        console.error('❌ Errore login:', error);
        throw error;
    }
}

/**
 * Refresh token - Usa il RefreshToken nel cookie httpOnly
 */
export async function refreshTokenWithCookie(): Promise<LoginResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/user/auth/refresh`, {
            method: 'POST',
            credentials: 'include' // Include cookie RefreshToken
        });
        
        if (!response.ok) {
            throw new Error('Refresh token fallito');
        }
        
        const data = await response.json();
        
        // Aggiorna informazioni utente
        if (data.user) {
            sessionStorage.setItem('user', JSON.stringify(data.user));
        }
        
        console.log('✅ Token refreshati via cookie httpOnly');
        return data;
        
    } catch (error) {
        console.error('❌ Errore refresh token:', error);
        throw error;
    }
}

/**
 * Logout - Cancella i cookie httpOnly
 */
export async function logoutWithCookies(): Promise<void> {
    try {
        await fetch(`${API_BASE_URL}/user/auth/logout`, {
            method: 'POST',
            credentials: 'include'
        });
        
        // Rimuovi dati utente da sessionStorage
        sessionStorage.removeItem('user');
        
        console.log('✅ Logout completato, cookie e token rimossi');
        
    } catch (error) {
        console.error('❌ Errore logout:', error);
        // Anche se la chiamata fallisce, pulisci il sessionStorage
        sessionStorage.removeItem('user');
    }
}

/**
 * Verifica stato autenticazione
 */
export async function checkAuthStatus(): Promise<StatusResponse> {
    try {
        const response = await fetch(`${API_BASE_URL}/user/auth/status`, {
            method: 'GET',
            credentials: 'include'
        });
        
        const data = await response.json();

        if (response.status === 401) {
            try {
                const refreshed = await refreshTokenWithCookie();
                if (refreshed.success && refreshed.user) {
                    return {
                        authenticated: true,
                        user: refreshed.user
                    };
                }
            } catch {
                return { authenticated: false };
            }
        }
        
        if (data.authenticated && data.user) {
            // Aggiorna sessionStorage con dati freschi
            sessionStorage.setItem('user', JSON.stringify(data.user));
        }
        
        return data;
        
    } catch (error) {
        console.error('❌ Errore verifica status:', error);
        return { authenticated: false };
    }
}

function notifySessionExpired(): void {
    sessionStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('auth:session-expired', {
        detail: { message: 'La tua sessione è scaduta. Effettua nuovamente il login.' }
    }));
}

/**
 * Interceptor per chiamate API - Gestisce automaticamente il refresh token
 */
export async function fetchWithAuth(
    url: string,
    options: RequestInit = {}
): Promise<Response> {
    // Emetti evento di inizio caricamento
    window.dispatchEvent(new CustomEvent('api:loading:start'));
    
    try {
        // Prima chiamata
        let response: Response;
        try {
            response = await fetch(url, {
                ...options,
                credentials: 'include' // Include sempre i cookie
            });
        } catch (requestError) {
            // Un errore CORS/rete non permette di sapere se il server ha
            // risposto 401: non va quindi trattato come sessione scaduta.
            console.error('❌ Chiamata API non leggibile (rete/CORS):', requestError);
            throw requestError;
        }
    
    // Se 401, prova refresh token automaticamente
    if (response.status === 401) {
        console.log('⏳ Token scaduto, tento refresh automatico...');
        try {
            await refreshTokenWithCookie();
        } catch (refreshError) {
            console.error('❌ Refresh token fallito - sessione scaduta');
            notifySessionExpired();
            throw new Error('Sessione scaduta');
        }

        // Un errore CORS/rete durante il retry non dimostra una sessione scaduta.
        response = await fetch(url, {
            ...options,
            credentials: 'include'
        });

        if (response.status === 401) {
            console.error('❌ La richiesta resta non autorizzata dopo il refresh');
            notifySessionExpired();
            throw new Error('Sessione scaduta');
        }

        console.log('✅ Chiamata riprovata con successo dopo refresh');
    }
    
    return response;
    
    } finally {
        // Emetti evento di fine caricamento (sempre, anche in caso di errore)
        window.dispatchEvent(new CustomEvent('api:loading:end'));
    }
}

/**
 * Wrapper per chiamate API che necessitano autenticazione
 * Usa fetchWithAuth che gestisce automaticamente il refresh
 */
export async function apiCall(
    endpoint: string,
    method: string = 'GET',
    body?: any
): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const options: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    };
    
    if (body) {
        options.body = JSON.stringify(body);
    }
    
    const response = await fetchWithAuth(url, options);
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore API');
    }
    
    return response.json();
}

/**
 * Verifica lo stato dell'utente (appartenenza al gruppo ClientUser)
 */
export async function checkUserStatus(): Promise<{ hasAccess: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/user/check_status_user`, {
        method: 'GET'
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore verifica status utente');
    }
    
    return response.json();
}

// ============================================================================
// WRAPPER API MODERNI CON GESTIONE AUTOMATICA REFRESH TOKEN
// ============================================================================
// Tutti questi wrapper usano fetchWithAuth che gestisce automaticamente:
// - Inclusione cookie HttpOnly con i token
// - Intercettazione 401 e refresh automatico
// - Retry della richiesta originale dopo refresh
// - Redirect a login se refresh fallisce
// ============================================================================

/**
 * Recupera lista attività dell'utente autenticato
 */
export async function getUserActivitiesModern(): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/user/activities`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Errore caricamento attività');
    }
    
    return response.json();
}

/**
 * Recupera lista lavori per una specifica attività
 */
export async function getWorksListModern(activityId: string): Promise<any> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/user/works/list?id_activity=${activityId}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    );
    
    if (!response.ok) {
        throw new Error('Errore caricamento lavori');
    }
    
    return response.json();
}

/**
 * Crea un nuovo lavoro
 */
export async function createWorkModern(workData: any): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/user/works/create`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(workData)
    });
    
    if (!response.ok) {
        throw new Error('Errore creazione lavoro');
    }
    
    return response.json();
}

/**
 * Recupera dettaglio di un lavoro specifico
 */
export async function getWorkDetailModern(workId: string): Promise<any> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/user/works/detail?id_work=${workId}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    );
    
    if (!response.ok) {
        throw new Error('Errore caricamento dettaglio lavoro');
    }
    
    return response.json();
}

/**
 * Aggiorna un lavoro esistente
 */
export async function updateWorkModern(workId: string, workData: any): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/user/works/update`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id_work: workId, ...workData })
    });
    
    if (!response.ok) {
        throw new Error('Errore aggiornamento lavoro');
    }
    
    return response.json();
}

/**
 * Elimina un lavoro
 */
export async function deleteWorkModern(workId: string): Promise<any> {
    const response = await fetchWithAuth(`${API_BASE_URL}/user/works/delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id_work: workId })
    });
    
    if (!response.ok) {
        throw new Error('Errore eliminazione lavoro');
    }
    
    return response.json();
}

/**
 * Recupera lista documenti per un lavoro
 */
export async function getDocumentsModern(workId: string): Promise<any> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/user/documents?id_work=${workId}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    );
    
    if (!response.ok) {
        throw new Error('Errore caricamento documenti');
    }
    
    return response.json();
}

/**
 * Invia fascicolo elettronico via email con link ai documenti
 */
export async function sendDocumentFolder(data: {
    workIds: string[];
    recipientEmail: string;
    ccEmail?: string;
    subject?: string;
    message?: string;
}): Promise<any> {
    const response = await fetchWithAuth(
        `${API_BASE_URL}/user/send-documents`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }
    );
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore invio email');
    }
    
    return response.json();
}

/**
 * Ottiene elenco pratiche per un'attività con conteggio documenti
 */
export async function getWorksSummaryForActivity(
    activityId: string,
    userId?: string | null
): Promise<{
    works: Array<{
        id_work: string;
        work_name: string;
        description: string;
        documentsCount: number;
        completion_count: number;
        status: string;
        completion_percentage: number;
    }>;
}> {
    // Costruisci URL con parametri
    let url = `${API_BASE_URL}/user/works/list?id_activity=${encodeURIComponent(activityId)}&include_document_count=true`;
    
    // Se è fornito userId (Admin visualizza dati di un altro utente), aggiungilo
    if (userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
    }
    
    const response = await fetchWithAuth(url, {
        method: 'GET',
        credentials: 'include'
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore caricamento pratiche');
    }
    
    return response.json();
}

/**
 * Ottiene i dettagli di una singola attività
 */
export async function getActivityDetails(
    activityId: string,
    userId?: string | null
): Promise<{
    activityId: string;
    indirizzo: string;
    citta: string;
    provincia: string;
    cap: string;
}> {
    // Costruisci URL con parametro userId opzionale per Admin
    let url = `${API_BASE_URL}/user/activities/${encodeURIComponent(activityId)}`;
    
    // Se è fornito userId (Admin visualizza dati di un altro utente), aggiungilo
    if (userId) {
        url += `?userId=${encodeURIComponent(userId)}`;
    }
    
    const response = await fetchWithAuth(url, {
        method: 'GET',
        credentials: 'include'
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore caricamento attività');
    }
    
    const data = await response.json();
    return data.activity; // La Lambda restituisce { success: true, activity: {...} }
}
